import type { ComponentSchema, FieldSchema } from '../../../../../schema-types'

/**
 * Content sections Neon Cinema renders with its own markup (sections/<type>/themes/lightings-1.vue), described by
 * the fields those overrides read. Colour fields the overrides ignore are left out: the theme keeps its tokens.
 */

const CONTENT_PAGES = ['home', 'shop', 'category', 'categories', 'product', 'cart', 'blog']

/** Icons the Neon Cinema kit draws; shared names such as "shipping" or "support" still map onto them. */
const ICON_OPTIONS = ['truck', 'cash', 'phone', 'shield', 'star', 'heart', 'package', 'check', 'home', 'map-pin', 'mail', 'bolt', 'spark', 'clock', 'bag', 'gift', 'headset', 'refresh', 'bulb', 'plug']
  .map(value => ({ label: value.replace('-', ' '), value }))

const headingFields = (defaults: { eyebrow?: string, title?: string, subtitle?: string } = {}): FieldSchema[] => [
  { key: 'eyebrow', label: 'Eyebrow', type: 'localized-string', default: defaults.eyebrow ?? '' },
  { key: 'title', label: 'Title', type: 'localized-string', default: defaults.title ?? '' },
  { key: 'subtitle', label: 'Subtitle', type: 'localized-text', default: defaults.subtitle ?? '' },
]

const POINT_ITEM: FieldSchema[] = [
  { key: 'icon', label: 'Icon', type: 'icon', default: 'shield', options: ICON_OPTIONS },
  { key: 'title', label: 'Title', type: 'localized-string' },
  { key: 'body', label: 'Text', type: 'localized-text' },
]

export const bannerCarouselSchema: ComponentSchema = {
  type: 'banner-carousel',
  label: 'Banner Carousel',
  description: 'Picture slides (their text is part of the image) crossfading in a framed screen, with a progress-bar pager.',
  supportedPages: ['home', 'shop', 'category', 'categories'],
  variants: [{
    id: 'v1',
    label: 'Framed screen',
    fields: [
      { key: 'title', label: 'Name for screen readers', type: 'string', default: 'Offers' },
      {
        key: 'slides',
        label: 'Slides',
        type: 'array',
        itemSchema: [
          { key: 'image', label: 'Picture', type: 'image', required: true },
          { key: 'mobileImage', label: 'Phone picture (optional)', type: 'image' },
          { key: 'alt', label: 'Picture description', type: 'string' },
          { key: 'link', label: 'Link', type: 'string', placeholder: '/shop' },
        ],
      },
      { key: 'autoplay', label: 'Play automatically', type: 'boolean', default: true },
      { key: 'autoplaySeconds', label: 'Seconds per slide', type: 'number', default: 5, dependency: { field: 'autoplay', value: true } },
    ],
  }],
  commonFields: [],
}

const TILE_FIELDS: FieldSchema[] = [
  ...headingFields({ eyebrow: 'Browse', title: 'Shop by category' }),
  {
    key: 'tiles',
    label: 'Tiles',
    type: 'array',
    help: 'Empty = the store\'s categories.',
    itemSchema: [
      { key: 'categoryId', label: 'Category', type: 'category-select', help: 'Sets the link, name and picture unless filled below.' },
      { key: 'title', label: 'Name', type: 'localized-string' },
      { key: 'tagline', label: 'Tagline', type: 'localized-string' },
      { key: 'image', label: 'Picture', type: 'image' },
      { key: 'link', label: 'Link', type: 'string', placeholder: '/category/…' },
    ],
  },
  { key: 'limit', label: 'Categories shown (automatic tiles)', type: 'number', default: 8 },
]

export const categoryBentoSchema: ComponentSchema = {
  type: 'category-bento',
  label: 'Category Tiles',
  description: 'Glowing round tiles (V1) or dark bento cards with the picture in a cyan glow (V2).',
  supportedPages: CONTENT_PAGES,
  variants: [
    { id: 'v1', label: 'V1 — Round tiles', fields: TILE_FIELDS },
    { id: 'v2', label: 'V2 — Bento cards', fields: TILE_FIELDS },
  ],
  commonFields: [],
}

export const comboSpotlightSchema: ComponentSchema = {
  type: 'combo-spotlight',
  label: 'Product Spotlight',
  description: 'One product or bundle on a dark stage under a cone of light, with its saving. Hidden until a product is picked.',
  supportedPages: CONTENT_PAGES,
  variants: [{
    id: 'v1',
    label: 'Spotlight stage',
    fields: [
      ...headingFields({ eyebrow: 'Bundle & save', title: 'Bundle of the week' }),
      { key: 'productId', label: 'Product', type: 'product-select' },
      { key: 'description', label: 'Text', type: 'localized-text', default: '' },
      { key: 'mrpLabel', label: 'Regular price label', type: 'localized-string', default: 'Regular price' },
      { key: 'savingLabel', label: 'Saving label', type: 'localized-string', default: 'You save' },
      { key: 'detailsLabel', label: 'Details button text', type: 'localized-string', default: 'View details' },
    ],
  }],
  commonFields: [],
}

export const corporateBandSchema: ComponentSchema = {
  type: 'corporate-band',
  label: 'Bulk Order Band',
  description: 'A dark panel edged by an amber-to-cyan line: eyebrow, title and text beside one button.',
  supportedPages: CONTENT_PAGES,
  variants: [{
    id: 'v1',
    label: 'Text and button',
    fields: [
      { key: 'eyebrow', label: 'Eyebrow', type: 'localized-string', default: 'For businesses' },
      { key: 'title', label: 'Title', type: 'localized-string', default: 'Bulk and project orders' },
      { key: 'text', label: 'Text', type: 'localized-text', default: 'Lighting an office, a venue or a new build? Get volume pricing and scheduled delivery.' },
      { key: 'cta.label', label: 'Button text', type: 'localized-string', default: 'Contact us' },
      { key: 'cta.url', label: 'Button link', type: 'string', default: '/contact' },
    ],
  }],
  commonFields: [],
}

const FEED_FIELDS: FieldSchema[] = [
  { key: 'title', label: 'Title', type: 'string' },
  { key: 'handle', label: 'Handle (e.g. @neoncinema)', type: 'string' },
  {
    key: 'items',
    label: 'Photos',
    type: 'array',
    itemSchema: [
      { key: 'image', label: 'Photo', type: 'image' },
      { key: 'link', label: 'Link', type: 'string' },
    ],
  },
]

export const instagramFeedSchema: ComponentSchema = {
  type: 'instagram-feed',
  label: 'Social Feed',
  description: 'Photo tiles that glow on hover, the handle as the eyebrow.',
  supportedPages: ['home', 'shop'],
  variants: [
    { id: 'v1', label: 'V1 — 5 columns', fields: FEED_FIELDS },
    { id: 'v2', label: 'V2 — 6 columns, handle on hover', fields: FEED_FIELDS },
    { id: 'v3', label: 'V3 — 4 large tiles', fields: FEED_FIELDS },
    { id: 'v4', label: 'V4 — Masonry', fields: FEED_FIELDS },
  ],
  commonFields: [],
}

export const newArrivalsSchema: ComponentSchema = {
  type: 'new-arrivals',
  label: 'New Arrivals',
  description: 'A rail of the newest products with "Shop all".',
  supportedPages: ['home', 'shop', 'category', 'categories'],
  variants: [{
    id: 'v1',
    label: 'Newest rail',
    fields: [
      { key: 'title', label: 'Title', type: 'string', default: 'New arrivals' },
      { key: 'subtitle', label: 'Eyebrow', type: 'string' },
    ],
  }],
  commonFields: [],
}

export const offerBannerSchema: ComponentSchema = {
  type: 'offer-banner',
  label: 'Offer Banner',
  description: 'A picture panel under a dark wash with an amber light leak, a large title and one button.',
  supportedPages: ['home', 'shop', 'category', 'categories'],
  variants: [{
    id: 'v1',
    label: 'Picture panel',
    fields: [
      { key: 'image', label: 'Image', type: 'image' },
      { key: 'title', label: 'Title', type: 'string', default: 'Special offer' },
      { key: 'subtitle', label: 'Subtitle', type: 'string' },
      { key: 'ctaText', label: 'Button text', type: 'string', default: 'Shop now' },
      { key: 'ctaLink', label: 'Button link', type: 'string', default: '/shop', help: 'The button shows only with a link.' },
      { key: 'overlayOpacity', label: 'Dark wash (40–90)', type: 'number', default: 55 },
    ],
  }],
  commonFields: [],
}

export const productRailSchema: ComponentSchema = {
  type: 'product-rail',
  label: 'Product Rail',
  description: 'Product cards from new arrivals, deals, a category or your own pick, as a grid or a rail. Hidden when empty.',
  supportedPages: CONTENT_PAGES,
  variants: [{
    id: 'v1',
    label: 'Product cards',
    fields: [
      ...headingFields({ eyebrow: 'Just in', title: 'New arrivals' }),
      { key: 'source', label: 'Products', type: 'product-source', default: { source: 'new', limit: 8 } },
      {
        key: 'layout',
        label: 'Layout',
        type: 'select',
        options: [{ label: 'Grid', value: 'grid' }, { label: 'Rail', value: 'rail' }],
        default: 'grid',
      },
      { key: 'showViewAll', label: 'Show "View all"', type: 'boolean', default: true },
      { key: 'viewAllLabel', label: '"View all" text', type: 'string', default: 'View all', dependency: { field: 'showViewAll', value: true } },
    ],
  }],
  commonFields: [],
}

export const promisesSchema: ComponentSchema = {
  type: 'promises',
  label: 'Promises Strip',
  description: 'Two to four glass tiles in one strip, each with a glowing icon, title and line.',
  supportedPages: CONTENT_PAGES,
  variants: [{
    id: 'v1',
    label: 'Glass strip',
    fields: [
      ...headingFields(),
      {
        key: 'items',
        label: 'Promises',
        type: 'array',
        itemSchema: POINT_ITEM,
        default: [
          { icon: 'truck', title: 'Fast delivery', body: 'Quick delivery across the city.' },
          { icon: 'cash', title: 'Pay on delivery', body: 'Pay in cash when your order arrives.' },
          { icon: 'refresh', title: 'Easy returns', body: 'Not right? We will make it right.' },
          { icon: 'headset', title: 'Here to help', body: 'Call or message us any day.' },
        ],
      },
    ],
  }],
  commonFields: [],
}

export const reviewsSchema: ComponentSchema = {
  type: 'reviews',
  label: 'Customer Reviews',
  description: 'A rail of glass review cards. The store\'s featured reviews when available, else the ones entered here; hidden when there are none.',
  supportedPages: CONTENT_PAGES,
  variants: [{
    id: 'v1',
    label: 'Review cards',
    fields: [
      ...headingFields({ eyebrow: 'Reviews', title: 'What customers say' }),
      {
        key: 'items',
        label: 'Reviews',
        type: 'array',
        itemSchema: [
          { key: 'rating', label: 'Rating (1–5)', type: 'number', default: 5 },
          { key: 'title', label: 'Title', type: 'localized-string' },
          { key: 'body', label: 'Review', type: 'localized-text' },
          { key: 'name', label: 'Customer name', type: 'string' },
          { key: 'productId', label: 'Product', type: 'product-select' },
        ],
      },
      { key: 'limit', label: 'Most reviews shown', type: 'number', default: 6 },
      { key: 'minRating', label: 'Lowest rating shown (store reviews)', type: 'number', default: 4 },
    ],
  }],
  commonFields: [],
}

export const storySchema: ComponentSchema = {
  type: 'story',
  label: 'Story Band',
  description: 'A picture in an amber spotlight beside two to four points on a luminous rail, with an optional button.',
  supportedPages: CONTENT_PAGES,
  variants: [{
    id: 'v1',
    label: 'Spotlight and points',
    fields: [
      ...headingFields({ eyebrow: 'Our promise', title: 'Why shop with us' }),
      { key: 'image', label: 'Picture', type: 'image' },
      { key: 'points', label: 'Points', type: 'array', itemSchema: POINT_ITEM },
      { key: 'ctaLabel', label: 'Button text', type: 'localized-string', default: '' },
      { key: 'ctaLink', label: 'Button link', type: 'string', default: '' },
    ],
  }],
  commonFields: [],
}
